import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { OUTLINE_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { Button, Space, Text, Group, Tooltip } from "@mantine/core";
import { useAccount } from "wagmi";
import { networkLookup } from "../../utils/blockExplorer";
import { getCurrencySymbol, getNativeCurrencyIcon } from "../../utils/currency";
import { useAuth } from "../auth";
import { FEATURE_FLAGS, useFeatureFlags } from "../auth/hooks";
import { useSupportedChains } from "../chain";
import { useModalStack } from "../modal";
import { CurrencyType } from "./BlockchainSelect";
import { SwapModal } from "./SwapModal";
import { useSwapModalContext } from "./SwapModalContext";
import { useCurrencyBalance } from "./useCurrencyBalance";
export const Balance = ({ small = false }) => {
    var _a;
    const { authenticated, walletAddress } = useAuth();
    const { swapModalRef } = useSwapModalContext();
    const account = useAccount();
    const [walletWrappedCurrency, setWalletWrappedCurrency] = useState(null);
    const [walletChainId, setCurrentWalletChainId] = useState(undefined);
    const { pushModal } = useModalStack();
    const enableSwapCurrency = useFeatureFlags(FEATURE_FLAGS.ENABLE_SWAP_CURRENCY);
    useEffect(() => {
        var _a;
        if ((_a = account === null || account === void 0 ? void 0 : account.chain) === null || _a === void 0 ? void 0 : _a.id) {
            setCurrentWalletChainId(account.chain.id);
        }
    }, [(_a = account === null || account === void 0 ? void 0 : account.chain) === null || _a === void 0 ? void 0 : _a.id]);
    const { supportedChains, loading: loadingChains, getWrappedCurrency, getChain, } = useSupportedChains(!walletAddress || !authenticated || !walletChainId);
    // Initialize wrappedCurrency
    useEffect(() => {
        if (!walletChainId || !supportedChains.length) {
            return setWalletWrappedCurrency(null);
        }
        const wrappedCurrency = getWrappedCurrency(walletChainId);
        if (!wrappedCurrency)
            return setWalletWrappedCurrency(null);
        setWalletWrappedCurrency({
            address: wrappedCurrency.address,
            symbol: wrappedCurrency.symbol,
            type: CurrencyType.WRAPPED,
            decimals: wrappedCurrency.decimals,
        });
    }, [walletChainId, supportedChains]);
    const { balanceRoundedSmall: nativeBalanceRounded, refetch: refetchNativeBalance, isFetching: isFetchingNativeBalance, } = useCurrencyBalance({ walletAddress, chainId: walletChainId });
    const { balanceRoundedSmall: wrappedBalanceRounded, refetch: refetchWrappedBalance, isFetching: isFetchingWrappedBalance, } = useCurrencyBalance({
        walletAddress,
        chainId: walletChainId,
        currency: walletWrappedCurrency,
    });
    const currencySymbol = walletChainId ? getCurrencySymbol(walletChainId) : "";
    const walletNetworkChain = walletChainId
        ? networkLookup(walletChainId)
        : undefined;
    const open = useCallback((configuration) => {
        if (!walletAddress || !walletChainId || !enableSwapCurrency)
            return;
        pushModal(_jsx(SwapModal, { supportedChains: supportedChains, walletAddress: walletAddress, walletWrappedCurrency: walletWrappedCurrency, getChain: getChain, walletChainId: walletChainId, isFetchingBalance: isFetchingNativeBalance || isFetchingWrappedBalance, prefillToChain: configuration === null || configuration === void 0 ? void 0 : configuration.prefillToChain, prefillToCurrency: configuration === null || configuration === void 0 ? void 0 : configuration.prefillToCurrency, small: small, refetchBalance: async () => {
                await Promise.all([
                    refetchNativeBalance(),
                    refetchWrappedBalance(),
                ]);
            } }), {
            fullScreen: small,
            size: "md",
            title: `Exchange currency across chains`,
        });
    }, [
        pushModal,
        currencySymbol,
        walletAddress,
        walletWrappedCurrency,
        walletChainId,
        supportedChains,
        isFetchingNativeBalance,
        isFetchingWrappedBalance,
        enableSwapCurrency,
    ]);
    useEffect(() => {
        swapModalRef.current = open;
    }, [open]);
    if (loadingChains ||
        !walletAddress ||
        !authenticated ||
        !walletChainId ||
        isFetchingNativeBalance ||
        isFetchingWrappedBalance) {
        return _jsx(_Fragment, {});
    }
    return (_jsx(_Fragment, { children: small ? (_jsxs(Group, { position: "apart", px: 12, py: 10, onClick: () => open(), children: [_jsx(Text, { size: "sm", children: "Balance" }), _jsxs(Text, { size: "sm", color: TEXT_COLOR.SECONDARY, children: [nativeBalanceRounded, " ", currencySymbol] })] })) : (_jsx(Tooltip, { label: "Exchange currency across chains", children: _jsxs(Button, { color: OUTLINE_COLOR, onClick: () => open(), children: [(walletNetworkChain === null || walletNetworkChain === void 0 ? void 0 : walletNetworkChain.type) && (_jsxs(_Fragment, { children: [getNativeCurrencyIcon(walletNetworkChain.type, 16), _jsx(Space, { w: 8 })] })), nativeBalanceRounded, " ", currencySymbol, walletWrappedCurrency
                        ? ` | ${wrappedBalanceRounded} ${walletWrappedCurrency.symbol}`
                        : ""] }) })) }));
};
